import { useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components/macro';
import first from 'lodash.first';
import last from 'lodash.last';
import AnimatedPath from '../AnimatedPath/AnimatedPath';

import getEntityKey from 'helpers/getEntityKey';
import generateRandomHash from 'helpers/generateRandomHash';
import computeSegments from '../LineChart/helpers/computeSegments';
import computeNotNullSegments from 'components/data-visualization/DoubleChart/utils/computeNotNullSegments';
import {
  number,
  oneOf,
  func,
  bool,
  shape,
  string,
  arrayOf,
  instanceOf,
  oneOfType,
} from 'prop-types';

const Path = styled(AnimatedPath)`
  fill: none;
  stroke-width: 2;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
  shape-rendering: auto;
`;

/**
 * PathWithFilledGaps renders a trend line (SVG <path> element). It optionally
 * fills up the gaps in a line with a dashed line. To achieve the desired
 * effect, it uses both clipPath and mask defs to clip and mask the relevant
 * parts of the dashed line and the non-dashed line so as to make two lines
 * appear as a single line.
 *
 * @param {object} props React props
 * @param {('date'|'periodKey')} props.dateKey The key to use to access the timestamp on trend data points
 * @param {number} props.height The height of the drawing area
 * @param {function} props.path Configured D3.js line() renderer
 * @param {boolean} props.showGapLines Should the gaps in this line be filled with a dashed line?
 * @param {object} props.trend The trend data to render a line for
 * @param {('value'|'position')} props.valueKey The key to use to access the value on trend data points
 * @param {number} props.width The width of the drawing area
 * @param {function} props.x Scale for the horizontal (x) axis
 */
function PathWithFilledGaps(props) {
  const {
    dateKey = 'date',
    height,
    path,
    showGapLines,
    closeGaps = false,
    trend: trendOrdered,
    width,
    x,
    isTimeBoxing = false,
    additionalKey = '',
    ...presentational
  } = props;

  const hasData = useCallback(path.defined(), [path]);
  const trend = isTimeBoxing
    ? { ...trendOrdered, data: trendOrdered.data.reverse() }
    : trendOrdered;
  const segments = useCallback(
    isTimeBoxing
      ? computeNotNullSegments(trend.data, 'value')
      : computeSegments(trend.data, hasData),
    [trend, hasData]
  );
  const hash = useRef(generateRandomHash());
  const filteredData = useMemo(
    () => trend.data.filter(hasData),
    [trend.data, hasData]
  );
  const xKey = isTimeBoxing ? 'index' : dateKey;

  // Don't render when the width and height haven't been set yet
  if (width === 0 || height === 0) return null;

  return (
    <g>
      <defs>
        {showGapLines && (
          <clipPath
            id={`line-clip-path-${getEntityKey(trend)}-${
              hash.current
            }${additionalKey}`}
          >
            {segments.map((segment) => {
              // This is supposed to be flipped! The first date is the latest, and is the end of the line
              const end = first(segment);
              const start = last(segment);

              return (
                <rect
                  fill={'black'}
                  key={`segment-${start.periodKey}-${end.periodKey}`}
                  x={x(start[xKey])}
                  y={0}
                  height={height}
                  width={x(end[xKey]) - x(start[xKey])}
                />
              );
            })}
          </clipPath>
        )}

        <mask
          id={`gaps-mask-${getEntityKey(trend)}-${
            hash.current
          }${additionalKey}`}
        >
          <rect x="0" y="0" width={width} height={height} fill="white" />
          {segments.map((segment) => {
            const end = first(segment);
            const start = last(segment);

            return (
              <rect
                fill={'black'}
                key={`inverse-segment-${start.periodKey}-${end.periodKey}`}
                x={x(start[xKey])}
                y={0}
                height={height}
                width={x(end[xKey]) - x(start[xKey])}
              />
            );
          })}
        </mask>
      </defs>

      {showGapLines && (
        <Path
          pathString={path(filteredData)}
          mask={`url(#gaps-mask-${getEntityKey(trend)}-${
            hash.current
          })${additionalKey}`}
          strokeDasharray={closeGaps ? '2 2' : '4 4'}
          opacity="0.6"
          {...presentational}
        />
      )}

      <Path
        pathString={path(showGapLines ? filteredData : trend.data)}
        clipPath={
          showGapLines
            ? `url(#line-clip-path-${getEntityKey(trend)}-${
                hash.current
              })${additionalKey}`
            : undefined
        }
        {...presentational}
      />
    </g>
  );
}

PathWithFilledGaps.propTypes = {
  dateKey: oneOf(['date', 'periodKey']),
  height: number.isRequired,
  path: func.isRequired,
  showGapLines: bool,
  closeGaps: bool,
  trend: shape({
    entity: shape({
      id: string.isRequired,
      type: string,
    }),
    breakdown: oneOfType([string, number]),
    data: arrayOf(
      shape({
        value: number,
        position: number,
        date: instanceOf(Date),
        periodKey: number.isRequired,
      })
    ).isRequired,
  }),
  valueKey: oneOf(['value', 'position']),
  width: number.isRequired,
  x: func.isRequired,
  stroke: string,
  isTimeBoxing: bool,
  strokeWidth: number,
  fill: string,
  additionalKey: string,
};

export default PathWithFilledGaps;
